import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Select, Tooltip, Typography } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { MatchType } from '../../interface';
import { isMatchWinner, matchWinnerReasonSymbol } from './activityMatch';
import { bracketRoundName, formatPlanClock } from './bracketBuilderUtils';
import {
    BRACKET_COLUMN_WIDTH,
    BRACKET_MATCH_HEIGHT,
    BRACKET_ROUND_HEADER_HEIGHT,
    layoutBracketView,
    type BracketConnector,
    type BracketLayoutMatch,
    type BracketRoundPosition,
} from './bracketViewLayout';
import './bracketView.css';

const { Text } = Typography;

const BRACKET_MIN_ZOOM = 0.4;
const BRACKET_MAX_ZOOM = 1.1;
const BRACKET_ZOOM_STEP = 0.1;

export type BracketViewParticipant = {
    id?: string;
    display_name?: string;
    noc?: string;
    draft_slot_key?: string;
    draft_slot_kind?: string;
    is_seed_locked?: boolean;
    from_bye?: boolean;
};

export type BracketViewMatch = BracketLayoutMatch & {
    winner_id?: string;
    reason?: string;
    rank?: number;
    participants?: BracketViewParticipant[];
    preview?: boolean;
    draft_slots?: Array<{
        key: string;
        kind: string;
        side: number;
        participant_id?: string;
        is_seed_locked?: boolean;
    }>;
    court?: number;
    group_key?: string;
    plan_date?: string;
    plan_start_at?: string | null;
    plan_end_at?: string | null;
};

export type OperatorBracketEmptyInput = {
    selectedCategoryId?: string | null;
    category?: {
        need_reload?: boolean;
        is_setup_done?: boolean;
        bracket_status?: 'not_created' | 'draft' | 'published';
    } | null;
    error?: string | null;
    hasMatches: boolean;
};

export function operatorBracketEmptyCopy(input: OperatorBracketEmptyInput): { title: string; detail?: string } | null {
    if (!input.selectedCategoryId) {
        return { title: 'Select a category to view the tournament bracket.' };
    }
    if (input.category?.need_reload) {
        return {
            title: 'Participant information has changed.',
            detail: 'Reload the category before viewing or editing the bracket.',
        };
    }
    if (input.category && !input.category.is_setup_done) {
        return {
            title: 'Finish Category Setup before viewing the bracket.',
            detail: 'Complete setup, then create a draft in Bracket management.',
        };
    }
    if (input.error) {
        return { title: input.error };
    }
    if (!input.hasMatches) {
        if (input.category?.is_setup_done && (input.category.bracket_status === 'not_created' || !input.category.bracket_status)) {
            return {
                title: 'This category does not have a bracket draft yet.',
                detail: 'Create the draft in Bracket management to preview the draw.',
            };
        }
        return { title: 'No matches available for this category.' };
    }
    return null;
}

export type TournamentBracketProps = {
    matches: BracketViewMatch[];
    roundNames: string[];
    hasExtraMatches?: boolean;
    layoutKey?: string | number;
    showScheduleMeta?: boolean;
    onMatchClick?: (match: BracketViewMatch, participantId?: string) => void;
    onOpenEdit?: (match: BracketViewMatch) => void;
    slotEditor?: BracketSlotEditor;
};

export type BracketSlotEditorOption = {
    value: string;
    label: React.ReactNode;
    searchLabel?: string;
    disabled?: boolean;
};

export type BracketSlotEditor = {
    enabled: boolean;
    busy?: boolean;
    optionsForSlot: (slotKey: string, currentParticipantId?: string) => BracketSlotEditorOption[];
    onChoose: (slotKey: string, participantId?: string) => void;
};

function clampBracketZoom(value: number): number {
    const rounded = Math.round(value * 10) / 10;
    return Math.min(BRACKET_MAX_ZOOM, Math.max(BRACKET_MIN_ZOOM, rounded));
}

function visibleMatchCardParticipants(match: BracketViewMatch): BracketViewParticipant[] {
    return (match.participants || []).filter(participant => {
        if (match.type === MatchType.ExtraMatch) {
            return true;
        }
        return participant.draft_slot_kind !== 'bye';
    });
}

function matchCardParticipantLabel(
    participant: BracketViewParticipant,
    matchState: string | undefined,
    index: number,
): string {
    if (participant.display_name) {
        return participant.display_name;
    }
    if (matchState === 'bye') {
        return '';
    }
    return `Participant ${index + 1}`;
}

function byeAdvancePlayer(match: BracketViewMatch): BracketViewParticipant | undefined {
    const people = (match.participants || []).filter(participant => (
        participant.draft_slot_kind !== 'bye' && participant.draft_slot_kind !== 'qualifier'
    ));
    if (match.winner_id) {
        const winner = people.find(participant => participant.id === match.winner_id);
        if (winner) {
            return winner;
        }
    }
    return people.find(participant => participant.id) || people.find(participant => participant.draft_slot_key);
}

function isCenterBracketMatch(match: BracketViewMatch): boolean {
    return match.type === MatchType.Final || match.type === MatchType.ThirdPlace;
}

function placeByePlayer(
    participants: BracketViewParticipant[],
    player: BracketViewParticipant,
) {
    const index = participants.findIndex(item => item.id && item.id === player.id);
    if (index >= 0) {
        const current = participants[index];
        participants[index] = {
            ...current,
            from_bye: Boolean(player.from_bye),
            draft_slot_key: player.draft_slot_key || current.draft_slot_key,
            draft_slot_kind: player.draft_slot_kind || current.draft_slot_kind,
            is_seed_locked: player.is_seed_locked ?? current.is_seed_locked,
        };
        return;
    }
    participants.push(player);
}

// Bye matches stay in the API payload. The sheet shows that player on the next
// bout, with the real feeder drawn like an extra match: the open side reads
// "Winner of {round}" and the feeder sits one card above. Edit mode keeps the
// player's slot and labels the row, because the editor has no connector line.
export function presentBracketMatches(
    matches: BracketViewMatch[],
    options: { labelByes?: boolean; roundNames?: string[] } = {},
): BracketViewMatch[] {
    const byes = matches.filter(match => match.state === 'bye');
    if (!byes.length) {
        return matches;
    }
    const hidden = new Set(byes.map(match => match.id));
    const incoming = new Map<string, BracketViewParticipant[]>();
    byes.forEach(bye => {
        if (!bye.next_match_id) {
            return;
        }
        const player = byeAdvancePlayer(bye);
        if (!player || (!options.labelByes && !player.id)) {
            return;
        }
        const placed: BracketViewParticipant = options.labelByes
            ? { ...player, from_bye: true }
            : {
                id: player.id,
                display_name: player.display_name,
                noc: player.noc,
            };
        const list = incoming.get(bye.next_match_id) || [];
        list.push(placed);
        incoming.set(bye.next_match_id, list);
    });

    const matchById = new Map(matches.map(match => [match.id, match]));
    const feedersByNext = new Map<string, BracketViewMatch[]>();
    matches.forEach(match => {
        if (hidden.has(match.id) || !match.next_match_id) {
            return;
        }
        const list = feedersByNext.get(match.next_match_id) || [];
        list.push(match);
        feedersByNext.set(match.next_match_id, list);
    });

    const anchoredIds = new Set<string>();
    const qualifierByNext = new Map<string, BracketViewParticipant>();
    byes.forEach(bye => {
        if (!bye.next_match_id) {
            return;
        }
        const feeders = feedersByNext.get(bye.next_match_id) || [];
        if (feeders.length !== 1) {
            return;
        }
        const feeder = feeders[0];
        const target = matchById.get(bye.next_match_id);
        if (!target || hidden.has(target.id)) {
            return;
        }
        // The final sits in the center column, so the feeder stays in its own
        // column. The open side is still labeled.
        if (!isCenterBracketMatch(target)) {
            anchoredIds.add(feeder.id);
        }
        qualifierByNext.set(bye.next_match_id, {
            display_name: `Winner of ${bracketRoundName(options.roundNames, feeder.round)}`,
            draft_slot_kind: 'qualifier',
        });
    });

    return matches.filter(match => !hidden.has(match.id)).map(match => {
        const additions = incoming.get(match.id) || [];
        const qualifier = qualifierByNext.get(match.id);
        const anchored = anchoredIds.has(match.id);
        if (!additions.length && !qualifier && !anchored) {
            return match;
        }
        const participants = [...(match.participants || [])];
        additions.forEach(player => placeByePlayer(participants, player));
        const feeder = (feedersByNext.get(match.id) || [])[0];
        const winnerAlreadyShown = Boolean(
            feeder?.winner_id && participants.some(item => item.id === feeder.winner_id),
        );
        const hasQualifier = participants.some(item => item.draft_slot_kind === 'qualifier');
        if (qualifier && participants.length < 2 && !winnerAlreadyShown && !hasQualifier) {
            participants.unshift(qualifier);
        }
        return {
            ...match,
            ...(anchored ? { attached_feeder: true } : {}),
            participants,
        };
    });
}

function slotIsEditable(participant: BracketViewParticipant, match: BracketViewMatch): boolean {
    return Boolean(
        match.preview &&
        participant.draft_slot_key &&
        participant.draft_slot_kind !== 'bye' &&
        participant.draft_slot_kind !== 'qualifier',
    );
}

function matchScheduleMeta(match: BracketViewMatch): string | null {
    if (match.state === 'bye') {
        return null;
    }
    const parts: string[] = [];
    if (match.group_key) {
        parts.push(match.group_key);
    }
    if (match.court) {
        parts.push(`Court ${match.court}`);
    }
    const start = formatPlanClock(match.plan_start_at);
    const end = formatPlanClock(match.plan_end_at);
    const timeRange = start && end ? `${start}–${end}` : start;
    const when = [match.plan_date, timeRange].filter(Boolean).join(' ');
    if (when) {
        parts.push(when);
    }
    return parts.length ? parts.join(' · ') : null;
}

function roundColumnHeight(
    matches: BracketViewMatch[],
    positions: Record<string, BracketRoundPosition>,
): number {
    let maxTop = 0;
    matches.forEach(match => {
        maxTop = Math.max(maxTop, positions[match.id]?.top ?? 0);
    });
    return maxTop + BRACKET_MATCH_HEIGHT + BRACKET_ROUND_HEADER_HEIGHT;
}

function cardTitle(match: BracketViewMatch, roundName: string): string {
    if (match.type === MatchType.ThirdPlace) {
        return '3rd Place';
    }
    if (match.state === 'bye') {
        return 'Bye';
    }
    const bout = match.bout_number?.trim();
    return bout ? `Bout ${bout}` : roundName;
}

function roundSessionNumbers(matches: BracketViewMatch[]): Record<string, number> {
    const byRound = new Map<number, BracketViewMatch[]>();
    matches.forEach((match) => {
        if (match.type === MatchType.ThirdPlace) {
            return;
        }
        const items = byRound.get(match.round) || [];
        items.push(match);
        byRound.set(match.round, items);
    });
    const sessions: Record<string, number> = {};
    byRound.forEach((items) => {
        [...items]
            .sort((left, right) => {
                const rankDelta = (left.rank ?? Number.MAX_SAFE_INTEGER) - (right.rank ?? Number.MAX_SAFE_INTEGER);
                if (rankDelta !== 0) {
                    return rankDelta;
                }
                return left.id.localeCompare(right.id);
            })
            .forEach((match, index) => {
                sessions[match.id] = index + 1;
            });
    });
    return sessions;
}

export function matchCardCanOpenEdit(
    match: Pick<BracketViewMatch, 'preview' | 'bout_number' | 'state'>,
    onOpenEdit?: TournamentBracketProps['onOpenEdit'],
): boolean {
    return Boolean(onOpenEdit && !match.preview && match.bout_number && match.state !== 'bye');
}

const MatchCard: React.FC<{
    match: BracketViewMatch;
    roundName: string;
    session: number;
    isHighlighted?: string;
    showScheduleMeta?: boolean;
    onMatchClick?: TournamentBracketProps['onMatchClick'];
    onOpenEdit?: TournamentBracketProps['onOpenEdit'];
    slotEditor?: TournamentBracketProps['slotEditor'];
}> = ({ match, roundName, isHighlighted, showScheduleMeta, onMatchClick, onOpenEdit, slotEditor }) => {
    const scheduleMeta = showScheduleMeta ? matchScheduleMeta(match) : null;
    const usesDraftSlots = Boolean(match.preview && match.draft_slots?.length);
    const cardParticipants = visibleMatchCardParticipants(match);

    const handleParticipantClick = (participant: BracketViewParticipant, event: React.MouseEvent) => {
        event.stopPropagation();
        if (slotEditor?.enabled && slotIsEditable(participant, match)) {
            return;
        }
        if (participant.id) {
            onMatchClick?.(match, participant.id);
        }
    };

    const handleOpenEdit = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (match.preview) {
            return;
        }
        onOpenEdit?.(match);
    };

    const canOpenEdit = matchCardCanOpenEdit(match, onOpenEdit);
    const title = cardTitle(match, roundName);

    return (
        <div className={`bracket-view-card${match.state === 'bye' ? ' bracket-view-card-bye' : ''}`}>
            <Tooltip title={scheduleMeta || undefined}>
                <div
                    className={`bracket-view-card-header${canOpenEdit ? ' bracket-view-card-header-editable' : ''}`}
                    onClick={canOpenEdit ? handleOpenEdit : undefined}
                >
                    <Text strong className="bracket-view-card-header-title" title={canOpenEdit ? 'Edit match' : title}>
                        <span className="bracket-view-card-header-label">{title}</span>
                        {canOpenEdit ? <span className="bracket-view-card-header-edit">Edit</span> : null}
                    </Text>
                    {match.type === MatchType.Final && <Text style={{ fontSize: 12 }}>Final</Text>}
                    {match.type === MatchType.ThirdPlace && <Text style={{ fontSize: 12 }}>Bronze</Text>}
                </div>
            </Tooltip>
            <div className="bracket-view-card-body">
                {cardParticipants.map((participant, index) => {
                    const editingSlot = Boolean(
                        slotEditor?.enabled && slotIsEditable(participant, match),
                    );
                    const isPlaceholder = !participant.id;
                    const clickable = !editingSlot && Boolean(participant.id);
                    const highlighted = Boolean(participant.id && participant.id === isHighlighted);
                    const winner = isMatchWinner(match, participant.id) && match.state !== 'bye';
                    // Blue and red bars mark the two sides of a bout. A bye has one player.
                    const sideClass = match.state === 'bye'
                        ? ''
                        : index === 0
                            ? 'bracket-view-participant-top'
                            : 'bracket-view-participant-bottom';
                    const className = [
                        'bracket-view-participant',
                        sideClass,
                        editingSlot ? 'bracket-view-participant-editing' : '',
                        clickable ? 'bracket-view-participant-clickable' : '',
                        highlighted ? 'bracket-view-participant-highlighted' : '',
                        !highlighted && winner ? 'bracket-view-participant-winner' : '',
                    ].filter(Boolean).join(' ');
                    const label = matchCardParticipantLabel(participant, match.state, index);
                    const reasonSymbol = !editingSlot && participant.id
                        ? matchWinnerReasonSymbol({
                            winner_id: match.winner_id || '',
                            reason: match.reason,
                            state: match.state || '',
                        }, participant.id)
                        : '';
                    return (
                        <div
                            key={participant.draft_slot_key || participant.id || index}
                            className={className}
                            onClick={event => handleParticipantClick(participant, event)}
                        >
                            {editingSlot && participant.draft_slot_key ? (
                                <Select
                                    size="small"
                                    variant="borderless"
                                    className="bracket-view-participant-select"
                                    value={participant.id}
                                    placeholder="Unassigned"
                                    allowClear
                                    showSearch
                                    virtual={false}
                                    defaultActiveFirstOption={false}
                                    disabled={slotEditor?.busy || participant.is_seed_locked}
                                    options={slotEditor?.optionsForSlot(
                                        participant.draft_slot_key,
                                        participant.id,
                                    )}
                                    onClick={event => event.stopPropagation()}
                                    onMouseDown={event => event.stopPropagation()}
                                    onClear={() =>
                                        slotEditor?.onChoose(participant.draft_slot_key!, undefined)
                                    }
                                    onSelect={(value, option) => {
                                        const selected = option?.value ?? value;
                                        if (selected == null || selected === '') {
                                            return;
                                        }
                                        slotEditor?.onChoose(
                                            participant.draft_slot_key!,
                                            String(selected),
                                        );
                                    }}
                                    filterOption={(input, option) =>
                                        String(option?.searchLabel || option?.label || '')
                                            .toLowerCase()
                                            .includes(input.trim().toLowerCase())
                                    }
                                />
                            ) : (
                                <Text
                                    className={`bracket-view-participant-name${isPlaceholder || participant.draft_slot_kind === 'qualifier' ? ' bracket-view-participant-placeholder' : ''}`}
                                    title={label}
                                >
                                    {participant.noc && (
                                        <Text className="bracket-view-participant-noc">{participant.noc}</Text>
                                    )}
                                    {label}
                                </Text>
                            )}
                            {participant.from_bye ? (
                                <Text className="bracket-view-bye-label">Bye</Text>
                            ) : null}
                            {reasonSymbol ? (
                                <Text className="bracket-view-reason" onClick={handleOpenEdit}>
                                    {reasonSymbol}
                                </Text>
                            ) : null}
                        </div>
                    );
                })}
                {!usesDraftSlots && match.state !== 'bye' && [
                    cardParticipants.length < 1 && (
                        <div key="tbd1" className="bracket-view-participant bracket-view-participant-top">
                            <Text className="bracket-view-participant-placeholder">TBD</Text>
                        </div>
                    ),
                    cardParticipants.length < 2 && (
                        <div key="tbd2" className="bracket-view-participant bracket-view-participant-bottom">
                            <Text className="bracket-view-participant-placeholder">TBD</Text>
                        </div>
                    ),
                ].filter(Boolean)}
            </div>
        </div>
    );
};

const BracketConnectors: React.FC<{
    connectors: BracketConnector[];
    isHighlighted: (startMatchId: string, endMatchId: string) => boolean;
}> = ({ connectors, isHighlighted }) => (
    <div className="bracket-view-connectors">
        <svg preserveAspectRatio="none">
            {connectors.map(connector => {
                const midX = (connector.startX + connector.endX) / 2;
                const highlighted = isHighlighted(connector.startMatchId, connector.endMatchId);
                return (
                    <path
                        key={`${connector.kind}-${connector.startMatchId}-${connector.endMatchId}`}
                        d={`M ${connector.startX} ${connector.startY} L ${midX} ${connector.startY} L ${midX} ${connector.endY} L ${connector.endX} ${connector.endY}`}
                        stroke={highlighted ? '#1890ff' : '#bbb'}
                        strokeWidth={highlighted ? 2 : 1}
                        fill="none"
                    />
                );
            })}
        </svg>
    </div>
);

const MatchRound: React.FC<{
    matches: BracketViewMatch[];
    roundName: string;
    sessions: Record<string, number>;
    positions: Record<string, BracketRoundPosition>;
    isMatchHighlighted?: (match: BracketViewMatch) => string | undefined;
    showScheduleMeta?: boolean;
    onMatchClick?: TournamentBracketProps['onMatchClick'];
    onOpenEdit?: TournamentBracketProps['onOpenEdit'];
    slotEditor?: TournamentBracketProps['slotEditor'];
}> = ({
    matches,
    roundName,
    sessions,
    positions,
    isMatchHighlighted,
    showScheduleMeta,
    onMatchClick,
    onOpenEdit,
    slotEditor,
}) => {
    if (matches.length === 0) {
        return null;
    }
    return (
        <div className="bracket-view-round" style={{ height: roundColumnHeight(matches, positions) }}>
            <div className="bracket-view-round-header">
                <Text strong>{roundName}</Text>
            </div>
            <div className="bracket-view-round-body">
                {matches.map(match => (
                    <div
                        key={match.id}
                        className="bracket-view-match"
                        style={{ top: positions[match.id]?.top ?? 0 }}
                        data-match-id={match.id}
                        data-type={match.type}
                    >
                        <MatchCard
                            match={match}
                            roundName={roundName}
                            session={sessions[match.id] || 1}
                            onMatchClick={onMatchClick}
                            onOpenEdit={onOpenEdit}
                            slotEditor={slotEditor}
                            showScheduleMeta={showScheduleMeta}
                            isHighlighted={isMatchHighlighted?.(match)}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export const TournamentBracket: React.FC<TournamentBracketProps> = ({
    matches,
    roundNames,
    hasExtraMatches = false,
    layoutKey,
    showScheduleMeta = false,
    onMatchClick,
    onOpenEdit,
    slotEditor,
}) => {
    const [highlightedParticipantId, setHighlightedParticipantId] = useState<string | null>(null);
    const [zoom, setZoom] = useState(1);
    const zoomRef = useRef(1);
    const scrollRef = useRef<HTMLDivElement>(null);
    zoomRef.current = zoom;

    const displayMatches = useMemo(
        () => presentBracketMatches(matches || [], {
            labelByes: Boolean(slotEditor?.enabled),
            roundNames,
        }),
        [matches, roundNames, slotEditor?.enabled],
    );
    const layout = useMemo(
        () => layoutBracketView(displayMatches, hasExtraMatches),
        [displayMatches, hasExtraMatches],
    );

    const applyZoom = useCallback((nextZoom: number, origin?: { x: number; y: number }) => {
        const scroller = scrollRef.current;
        const clamped = clampBracketZoom(nextZoom);
        const previous = zoomRef.current;
        if (clamped === previous) {
            return;
        }
        if (scroller) {
            const rect = scroller.getBoundingClientRect();
            const focusX = origin ? origin.x - rect.left : scroller.clientWidth / 2;
            const focusY = origin ? origin.y - rect.top : scroller.clientHeight / 2;
            const contentX = (scroller.scrollLeft + focusX) / previous;
            const contentY = (scroller.scrollTop + focusY) / previous;
            zoomRef.current = clamped;
            setZoom(clamped);
            requestAnimationFrame(() => {
                scroller.scrollLeft = contentX * clamped - focusX;
                scroller.scrollTop = contentY * clamped - focusY;
            });
            return;
        }
        zoomRef.current = clamped;
        setZoom(clamped);
    }, []);

    useEffect(() => {
        zoomRef.current = 1;
        setZoom(1);
    }, [layoutKey]);

    useEffect(() => {
        const scroller = scrollRef.current;
        if (!scroller) {
            return undefined;
        }
        const onWheel = (event: WheelEvent) => {
            if (!event.ctrlKey && !event.metaKey) {
                return;
            }
            event.preventDefault();
            const delta = event.deltaY > 0 ? -BRACKET_ZOOM_STEP : BRACKET_ZOOM_STEP;
            applyZoom(zoomRef.current + delta, { x: event.clientX, y: event.clientY });
        };
        scroller.addEventListener('wheel', onWheel, { passive: false });
        return () => scroller.removeEventListener('wheel', onWheel);
    }, [applyZoom]);

    const handleMatchClick = (match: BracketViewMatch, participantId?: string) => {
        if (onMatchClick) {
            onMatchClick(match, participantId);
        }
        if (!participantId) {
            return;
        }
        setHighlightedParticipantId(current => current === participantId ? null : participantId);
    };

    const isMatchHighlighted = (match: BracketViewMatch) => {
        if (!highlightedParticipantId) {
            return undefined;
        }
        return match.participants?.find(participant => participant.id === highlightedParticipantId)?.id;
    };

    const isConnectorHighlighted = (startMatchId: string, endMatchId: string) => {
        if (!highlightedParticipantId) {
            return false;
        }
        const startMatch = matches.find(match => match.id === startMatchId);
        const endMatch = matches.find(match => match.id === endMatchId);
        if (!startMatch || !endMatch) {
            return false;
        }
        const startHas = startMatch.participants?.some(participant => participant.id === highlightedParticipantId);
        const endHas = endMatch.participants?.some(participant => participant.id === highlightedParticipantId);
        if (startHas && isMatchWinner(startMatch, highlightedParticipantId)) {
            return true;
        }
        if (endHas && isMatchWinner(endMatch, highlightedParticipantId)) {
            return true;
        }
        return false;
    };

    if (!displayMatches.length || !displayMatches.some(match => match.type === MatchType.Final)) {
        return (
            <div style={{ padding: 20, textAlign: 'center' }}>
                <Text>Bracket has not been generated yet.</Text>
            </div>
        );
    }

    const { leftMatches, rightMatches, centerMatches, leftPositions, rightPositions, centerPositions, connectors } = layout;
    const finalMatch = centerMatches.find(match => match.type === MatchType.Final);
    const thirdMatch = centerMatches.find(match => match.type === MatchType.ThirdPlace);
    const sessions = roundSessionNumbers(displayMatches);

    return (
        <div className="bracket-view">
            <div ref={scrollRef} className="bracket-view-scroll">
                <div className="bracket-view-canvas" style={{ zoom }}>
                    <BracketConnectors
                        connectors={connectors}
                        isHighlighted={isConnectorHighlighted}
                    />
                    <div className="bracket-view-board">
                        {layout.leftRounds.length > 0 && (
                            <div
                                className="bracket-view-side"
                                style={{ width: layout.leftRounds.length * BRACKET_COLUMN_WIDTH }}
                            >
                                {layout.leftRounds.map(round => (
                                    <MatchRound
                                        key={round}
                                        positions={leftPositions}
                                        matches={leftMatches.filter(match => match.round === round)}
                                        roundName={bracketRoundName(roundNames, round)}
                                        sessions={sessions}
                                        onMatchClick={handleMatchClick}
                                        onOpenEdit={onOpenEdit}
                                        slotEditor={slotEditor}
                                        showScheduleMeta={showScheduleMeta}
                                        isMatchHighlighted={isMatchHighlighted}
                                    />
                                ))}
                            </div>
                        )}
                        {centerMatches.length > 0 && (
                            <div className="bracket-view-center">
                                <div
                                    className="bracket-view-center-body"
                                    style={{ height: roundColumnHeight(centerMatches, centerPositions) }}
                                >
                                    {finalMatch && (
                                        <div
                                            className="bracket-view-match"
                                            style={{ top: centerPositions[finalMatch.id]?.top ?? 0 }}
                                            data-match-id={finalMatch.id}
                                            data-type={finalMatch.type}
                                        >
                                            <MatchCard
                                                match={finalMatch}
                                                roundName={bracketRoundName(roundNames, finalMatch.round)}
                                                session={sessions[finalMatch.id] || 1}
                                                onMatchClick={handleMatchClick}
                                                onOpenEdit={onOpenEdit}
                                                slotEditor={slotEditor}
                                                showScheduleMeta={showScheduleMeta}
                                                isHighlighted={isMatchHighlighted(finalMatch)}
                                            />
                                        </div>
                                    )}
                                    {thirdMatch && (
                                        <div
                                            className="bracket-view-match"
                                            style={{ top: centerPositions[thirdMatch.id]?.top ?? 0 }}
                                            data-match-id={thirdMatch.id}
                                            data-type={thirdMatch.type}
                                        >
                                            <MatchCard
                                                match={thirdMatch}
                                                roundName={bracketRoundName(roundNames, thirdMatch.round)}
                                                session={sessions[thirdMatch.id] || 1}
                                                onMatchClick={handleMatchClick}
                                                onOpenEdit={onOpenEdit}
                                                slotEditor={slotEditor}
                                                showScheduleMeta={showScheduleMeta}
                                                isHighlighted={isMatchHighlighted(thirdMatch)}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {layout.rightRounds.length > 0 && (
                            <div
                                className="bracket-view-side bracket-view-side-right"
                                style={{ width: layout.rightRounds.length * BRACKET_COLUMN_WIDTH }}
                            >
                                {layout.rightRounds.map(round => (
                                    <MatchRound
                                        key={round}
                                        positions={rightPositions}
                                        matches={rightMatches.filter(match => match.round === round)}
                                        roundName={bracketRoundName(roundNames, round)}
                                        sessions={sessions}
                                        onMatchClick={handleMatchClick}
                                        onOpenEdit={onOpenEdit}
                                        slotEditor={slotEditor}
                                        showScheduleMeta={showScheduleMeta}
                                        isMatchHighlighted={isMatchHighlighted}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="bracket-view-zoom">
                <Tooltip title="Zoom out">
                    <Button
                        size="small"
                        icon={<ZoomOutOutlined />}
                        disabled={zoom <= BRACKET_MIN_ZOOM}
                        onClick={() => applyZoom(zoom - BRACKET_ZOOM_STEP)}
                    />
                </Tooltip>
                <Tooltip title="Reset to 100%. Ctrl/⌘ + scroll also zooms.">
                    <Button size="small" type="text" onClick={() => applyZoom(1)} style={{ minWidth: 52 }}>
                        {Math.round(zoom * 100)}%
                    </Button>
                </Tooltip>
                <Tooltip title="Zoom in">
                    <Button
                        size="small"
                        icon={<ZoomInOutlined />}
                        disabled={zoom >= BRACKET_MAX_ZOOM}
                        onClick={() => applyZoom(zoom + BRACKET_ZOOM_STEP)}
                    />
                </Tooltip>
            </div>
        </div>
    );
};

export default TournamentBracket;
